// src/context/NotificationContext.tsx
import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';

export type NotificationType = 'success' | 'error' | 'info';

interface NotificationContextType {
    showNotification: (message: string, type?: NotificationType) => void;
}

interface NotificationState {
    message: string;
    type: NotificationType;
    isVisible: boolean;
}

// Criamos dois contextos: um para o estado e outro para a função
const NotificationStateContext = createContext<NotificationState | undefined>(undefined);
const NotificationDispatchContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
    const [notification, setNotification] = useState<NotificationState>({ message: '', type: 'info', isVisible: false });

    const showNotification = useCallback((message: string, type: NotificationType = 'info') => {
        setNotification({ message, type, isVisible: true });
        // Esconde a notificação após 3 segundos
        setTimeout(() => {
            setNotification(prev => ({ ...prev, isVisible: false }));
        }, 3000);
    }, []);

    return (
        <NotificationStateContext.Provider value={notification}>
            <NotificationDispatchContext.Provider value={{ showNotification }}>
                {children}
            </NotificationDispatchContext.Provider>
        </NotificationStateContext.Provider>
    );
}

// Hooks customizados para usar o contexto facilmente
export function useNotificationState() {
    const context = useContext(NotificationStateContext);
    if (context === undefined) {
        throw new Error('useNotificationState must be used within a NotificationProvider');
    }
    return context;
}

export function useNotificationDispatch() {
    const context = useContext(NotificationDispatchContext);
    if (context === undefined) {
        throw new Error('useNotificationDispatch must be used within a NotificationProvider');
    }
    return context;
}